import { attributionApiService } from './api/attributionService';

const STORAGE_VISIT_ID_KEY = 'attribution_visit_id';
const STORAGE_DATA_KEY = 'attribution_data';
const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function readStoredData() {
  try {
    const raw = localStorage.getItem(STORAGE_DATA_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.expiresAt && parsed.expiresAt < Date.now()) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

class AttributionTrackingService {
  constructor() {
    this.loadStateFromStorage();
  }

  loadStateFromStorage() {
    const visitId = localStorage.getItem(STORAGE_VISIT_ID_KEY);
    const attributionData = readStoredData();

    if (visitId && attributionData && this.validateReferralData(attributionData)) {
      this.visitId = visitId;
      this.currentRef = attributionData.ref;
      this.currentUtmSource = attributionData.utm_source;
      this.isAttributedSession = true;
    } else if (visitId || localStorage.getItem(STORAGE_DATA_KEY)) {
      // Stale or invalid — clean up
      this.cleanupStaleSession();
    } else {
      this.visitId = null;
      this.currentRef = null;
      this.currentUtmSource = null;
      this.isAttributedSession = false;
    }
  }

  generateVisitId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  validateReferralData(referralData) {
    if (!referralData) return false;
    if (referralData.ref && referralData.utm_source) return true;
    // A Google Ads click id is a valid attributed (paid) visit on its own —
    // ref/utm_source may be absent when auto-tagging is the only tagging.
    if (referralData.gclid || referralData.gbraid || referralData.wbraid) return true;
    return false;
  }

  getOrCreateVisitId() {
    let visitId = localStorage.getItem(STORAGE_VISIT_ID_KEY);
    if (!visitId) {
      visitId = this.generateVisitId();
      localStorage.setItem(STORAGE_VISIT_ID_KEY, visitId);
    }
    return visitId;
  }

  cleanupStaleSession() {
    localStorage.removeItem(STORAGE_VISIT_ID_KEY);
    localStorage.removeItem(STORAGE_DATA_KEY);
    this.visitId = null;
    this.currentRef = null;
    this.currentUtmSource = null;
    this.isAttributedSession = false;
  }

  // Seed local state from a magic-link URL (cross-browser conversion path).
  // Called on /verify when ?visitId=… is present and local storage is empty.
  hydrateFromVisitId(visitId) {
    if (!visitId) return false;
    if (localStorage.getItem(STORAGE_VISIT_ID_KEY)) return false;

    localStorage.setItem(STORAGE_VISIT_ID_KEY, visitId);
    // ref/utm aren't carried in the URL — server-side row already has them.
    // Store a minimal record so trackConversion can fire; we mark with __rehydrated
    // so callers can tell apart a real attributed session if needed.
    const minimal = {
      visitId,
      ref: '__rehydrated',
      utm_source: '__rehydrated',
      campaign: '',
      expiresAt: Date.now() + ATTRIBUTION_TTL_MS,
      __rehydrated: true,
    };
    localStorage.setItem(STORAGE_DATA_KEY, JSON.stringify(minimal));
    this.visitId = visitId;
    this.isAttributedSession = true;
    return true;
  }

  async trackVisit(referralData) {
    try {
      if (!this.validateReferralData(referralData)) {
        this.cleanupStaleSession();
        return null;
      }

      const visitId = this.getOrCreateVisitId();
      this.visitId = visitId;
      this.isAttributedSession = true;
      this.currentRef = referralData.ref;
      this.currentUtmSource = referralData.utm_source;

      const storedData = {
        visitId,
        ref: referralData.ref,
        utm_source: referralData.utm_source,
        utm_medium: referralData.utm_medium || '',
        utm_term: referralData.utm_term || '',
        campaign: referralData.campaign || '',
        gclid: referralData.gclid || '',
        gbraid: referralData.gbraid || '',
        wbraid: referralData.wbraid || '',
        expiresAt: Date.now() + ATTRIBUTION_TTL_MS,
      };
      localStorage.setItem(STORAGE_DATA_KEY, JSON.stringify(storedData));

      const result = await attributionApiService.trackVisit({
        visitId,
        refId: referralData.ref,
        utmSourceId: referralData.utm_source,
        landingPage: referralData.landingPage,
        campaign: referralData.campaign,
        utmMedium: referralData.utm_medium,
        utmTerm: referralData.utm_term,
        gclid: referralData.gclid,
        gbraid: referralData.gbraid,
        wbraid: referralData.wbraid
      });

      if (result.success) {
        return visitId;
      } else {
        throw new Error(result.error || 'Failed to track visit');
      }
    } catch (error) {
      console.error('Attribution tracking error:', error);
      this.cleanupStaleSession();
      return null;
    }
  }

  async trackPageView() {
    const visitId = localStorage.getItem(STORAGE_VISIT_ID_KEY);
    const attributionData = readStoredData();

    if (!visitId || !attributionData) {
      this.isAttributedSession = false;
      return;
    }

    if (!this.validateReferralData(attributionData) && !attributionData.__rehydrated) {
      this.cleanupStaleSession();
      return;
    }

    this.isAttributedSession = true;

    try {
      const result = await attributionApiService.trackPageView(visitId, window.location.pathname);
      if (!result.success) {
        console.warn('Failed to track page view:', result.error);
      }
    } catch (error) {
      console.error('Attribution page view tracking error:', error);
    }
  }

  async trackConversion(userId, displayName) {
    const visitId = localStorage.getItem(STORAGE_VISIT_ID_KEY);
    const attributionData = readStoredData();

    if (!visitId || !attributionData) {
      this.isAttributedSession = false;
      return;
    }

    this.isAttributedSession = true;

    try {
      const result = await attributionApiService.trackConversion(visitId, userId, displayName);

      if (result.success) {
        console.log('Conversion tracked successfully for visitId:', visitId);
        this.cleanupStaleSession();
      } else {
        console.warn('Failed to track conversion:', result.error);
      }
    } catch (error) {
      console.error('Attribution conversion tracking error:', error);
    }
  }
}

export const attributionTracking = new AttributionTrackingService();
